import { allVulnerabilitiesForAsset, vulnSeverityTier, vulnAckKey } from "./assetUtils";

// A network-wide "security score" is inherently a made-up heuristic — there's no
// ground truth for it. These weights are the only place that encodes the heuristic,
// so they can be retuned later without touching the scoring logic below.
//
// CVE volume is NOT scored twice here: dynamic_risk_score already folds CVE severity
// in as one of its weighted factors (the vuln_* risk_factors), so a full raw-CVE-count
// penalty on top of it would double-count the same signal. The only thing dynamic_risk_score
// can't see is dashboard acknowledgement (it's computed by the probe on a schedule from
// telemetry, with no notion of a user dismissing a finding here) — so the CVE term below
// is deliberately small and only tracks *unacknowledged* critical/high CVEs, as an
// immediate, actionable nudge: acking a finding should visibly move the score without
// re-litigating the full severity weighting dynamic_risk_score already did.
const UNACKED_CVE_PENALTY = { critical: 3, high: 1 };
const MAX_UNACKED_PENALTY = 10;
const ZONE_RISK_PENALTY = { critical: 6, high: 3 };
const MAX_ZONE_PENALTY = 20;

// Each asset now carries its own 0–100 dynamic_risk_score from the probe, which factors in
// things a raw CVE count can't see — role in the process, Purdue proximity, EOL OS,
// control-protocol reach, as well as CVE severity itself. This is the dominant term
// (70 of the 100-point budget) since it's the most complete signal we have. Assets are
// aggregated per VLAN/zone first (a power mean, not a plain average) so that the few
// riskiest assets in a VLAN dominate that VLAN's number instead of being diluted by a
// large population of low-risk ones; the per-VLAN numbers are then combined the same
// way, so the riskiest VLAN dominates the network-wide penalty rather than being
// averaged away by calmer VLANs.
const HIGH_RISK_THRESHOLD = 60;    // used only for the human-readable "reasons" text below
const MAX_ASSET_RISK_PENALTY = 70;
const RISK_POWER_MEAN_P = 3;       // higher p skews the aggregate toward the riskiest values, closer to max() than a plain average

// Power mean of p over the values, ignoring non-numeric entries. null if none are numeric.
const powerMean = (values, p = RISK_POWER_MEAN_P) => {
  const nums = values.filter((v) => typeof v === "number" && !Number.isNaN(v));
  if (nums.length === 0) return null;
  const sumPow = nums.reduce((sum, v) => sum + Math.pow(Math.max(v, 0), p), 0);
  return Math.pow(sumPow / nums.length, 1 / p);
};

const GRADE_BANDS = [
  { min: 90, grade: "A", color: "#4cceac" },
  { min: 75, grade: "B", color: "#73ccd7" },
  { min: 60, grade: "C", color: "#ffb422" },
  { min: 40, grade: "D", color: "#ff7744" },
  { min: 0,  grade: "F", color: "#ff4444" },
];

const gradeFor = (score) => GRADE_BANDS.find((b) => score >= b.min) ?? GRADE_BANDS[GRADE_BANDS.length - 1];

const pluralize = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// { rows, zones, acks } → { score, grade, color, reasons }
// rows/zones are the same shapes already used across HostsOnlineGrid/AssetsKpiBar.
export const computeSecurityScore = ({ rows = [], zones = [], acks = [] }) => {
  const ackSet = new Set((acks || []).map((a) => vulnAckKey(a.asset_id, a.cve_id, a.origin)));

  const bySeverity = { critical: 0, high: 0, medium: 0, low: 0 };
  let highRiskAssets = 0;
  const scoresByVlan = new Map(); // zone_id (or "__unassigned__") -> dynamic_risk_score[]

  rows.forEach((row) => {
    allVulnerabilitiesForAsset(row).forEach((v) => {
      if (ackSet.has(vulnAckKey(row.id, v.cve_id, v._originKey))) return; // acknowledged — doesn't count against the score
      const tier = vulnSeverityTier(v);
      if (tier in bySeverity) bySeverity[tier]++;
    });

    const effectiveScore = typeof row.dynamic_risk_score === "number"
      ? row.dynamic_risk_score
      : (typeof row.risk_score === "number" ? row.risk_score : null);
    if (effectiveScore === null) return;

    if (effectiveScore >= HIGH_RISK_THRESHOLD) highRiskAssets++;

    const vlanKey = row.zone_id ?? "__unassigned__";
    if (!scoresByVlan.has(vlanKey)) scoresByVlan.set(vlanKey, []);
    scoresByVlan.get(vlanKey).push(effectiveScore);
  });

  const unackedPenalty = Math.min(
    MAX_UNACKED_PENALTY,
    (bySeverity.critical * UNACKED_CVE_PENALTY.critical) + (bySeverity.high * UNACKED_CVE_PENALTY.high)
  );

  const riskyZoneCount = (zones || []).filter((z) => z.risk === "critical" || z.risk === "high").length;
  const zonePenalty = Math.min(
    MAX_ZONE_PENALTY,
    (zones || []).reduce((sum, z) => sum + (ZONE_RISK_PENALTY[z.risk] ?? 0), 0)
  );

  // Per-VLAN power mean first (riskiest assets dominate their own VLAN's number), then
  // the per-VLAN numbers are combined the same way (the riskiest VLAN dominates the network number).
  const vlanAggregates = Array.from(scoresByVlan.values())
    .map((scores) => powerMean(scores))
    .filter((v) => v !== null);
  const vlanRiskAggregate = powerMean(vlanAggregates) ?? 0; // 0–100 scale
  const assetRiskPenalty = Math.min(MAX_ASSET_RISK_PENALTY, (vlanRiskAggregate / 100) * MAX_ASSET_RISK_PENALTY);

  const score = Math.max(0, Math.min(100, Math.round(100 - unackedPenalty - zonePenalty - assetRiskPenalty)));
  const { grade, color } = gradeFor(score);

  const reasons = [];
  const totalUnacked = bySeverity.critical + bySeverity.high + bySeverity.medium + bySeverity.low;
  if (bySeverity.critical > 0) reasons.push(`${pluralize(bySeverity.critical, "unacknowledged critical CVE")}`);
  if (bySeverity.high > 0) reasons.push(`${pluralize(bySeverity.high, "unacknowledged high CVE")}`);
  if (highRiskAssets > 0) reasons.push(`${pluralize(highRiskAssets, "asset")} scoring ${HIGH_RISK_THRESHOLD} or above on their own risk score`);
  if (riskyZoneCount > 0) reasons.push(`${pluralize(riskyZoneCount, "zone")} manually flagged high/critical risk`);
  if (totalUnacked === 0 && riskyZoneCount === 0 && highRiskAssets === 0) reasons.push("No open findings — nothing dragging the score down right now.");

  // How much each factor is dragging the score down, as % of its own cap — distinct
  // from the raw counts shown in the stat tiles, so the score card doesn't just repeat
  // the same numbers a second time.
  const breakdown = [
    { key: "assetRisk", label: "Asset Risk", penalty: assetRiskPenalty, max: MAX_ASSET_RISK_PENALTY },
    { key: "zoneRisk", label: "Zone Risk", penalty: zonePenalty, max: MAX_ZONE_PENALTY },
    { key: "unackedCves", label: "Unacknowledged CVEs", penalty: unackedPenalty, max: MAX_UNACKED_PENALTY },
  ];

  return { score, grade, color, reasons, breakdown, bySeverity, highRiskAssets, vlanRiskAggregate };
};

// Same exact scoring logic as computeSecurityScore, scoped to a single zone —
// filters rows/zones down to that one zone and hands them to the same function,
// so a zone's score and the network-wide score never drift apart into two
// separately-maintained formulas. { score, grade, color, reasons, breakdown, ... }
// or null if the zone can't be resolved from `zones`.
export const computeZoneSecurityScore = (zoneId, { rows = [], zones = [], acks = [] } = {}) => {
  if (zoneId === null || zoneId === undefined) return null;
  const zone = (zones || []).find((z) => String(z._id ?? z.id) === String(zoneId));
  if (!zone) return null;
  const zoneRows = (rows || []).filter((r) => String(r.zone_id ?? "") === String(zoneId));
  return computeSecurityScore({ rows: zoneRows, zones: [zone], acks });
};
